import 'reflect-metadata';
import {
  Expose,
  Type,
  plainToInstance,
  instanceToPlain,
} from 'class-transformer';
import { UserStatus } from './enums/UserStatus';

export class Genealogy {
  // @Expose({ name: 'genealogy_edges' })
  // @Type(() => GenealogyEdge)
  // edges: GenealogyEdge[];

  @Expose({ name: 'affiliates' })
  @Type(() => GenealogyUser)
  users: GenealogyUser[];

  @Type(() => GenealogyNode)
  genealogy: GenealogyNode;

  @Expose({ name: 'search_valid' })
  searchValid: boolean;

  @Expose({ name: 'parent_username' })
  parentUsername?: string;

  @Expose({ name: 'max_levels' })
  maxLevels: number;

  @Expose({ name: 'max_width' })
  maxWidth?: number;

  @Expose({ name: 'root_affiliate_id' })
  rootAffiliateId: number;

  constructor(
    // edges: GenealogyEdge[],
    users: GenealogyUser[],
    genealogy: GenealogyNode,
    searchValid: boolean,
    parentUsername: string | undefined,
    maxLevels: number,
    rootAffiliateId: number,
    maxWidth: number | undefined
  ) {
    // this.edges = edges;
    this.users = users;
    this.genealogy = genealogy;
    this.searchValid = searchValid;
    this.parentUsername = parentUsername;
    this.maxLevels = maxLevels;
    this.maxWidth = maxWidth;
    this.rootAffiliateId = rootAffiliateId;
  }

  static fromJson(json: Record<string, any>): Genealogy {
    return plainToInstance(Genealogy, json);
  }

  toJson(): any {
    return instanceToPlain(this);
  }
}

export class GenealogyEdge {
  id: number;

  @Expose({ name: 'parent_id' })
  parentId: number;

  constructor(id: number, parentId: number) {
    this.id = id;
    this.parentId = parentId;
  }

  static fromJson(json: Record<string, any>): GenealogyEdge {
    return plainToInstance(GenealogyEdge, json);
  }

  toJson(): any {
    return instanceToPlain(this);
  }
}

export class GenealogyUser {
  id: number;

  @Expose({ name: 'parent_id' })
  parentId: number;

  position: number;

  status: UserStatus;

  username: string;

  avatar?: string;

  pv: string;

  tv: string;

  cv: string;

  @Expose({ name: 'created_at' })
  @Type(() => Date)
  dateCreated: Date;

  @Expose({ name: 'rank_id' })
  rankId?: number;

  @Expose({ name: 'rank_name' })
  rankName?: string;

  @Expose({ name: 'rank_icon' })
  rankIcon?: string;

  @Expose({ name: 'package_name' })
  packageName?: string;

  @Expose({ name: 'first_name' })
  firstname: string;

  @Expose({ name: 'last_name' })
  lastname?: string;

  level: number;

  @Expose({ name: 'total_earnings' })
  totalEarnings: string;

  @Expose({ name: 'downlines_count' })
  downlinesCount: number;

  @Expose({ name: 'total_left' })
  leftPoints?: string;

  @Expose({ name: 'total_right' })
  rightPoints?: string;

  @Expose({ name: 'carry_left' })
  leftCarry?: string;

  @Expose({ name: 'carry_right' })
  rightCarry?: string;

  @Expose({ name: 'left_downline_count' })
  leftDownlinesCount?: number;

  @Expose({ name: 'right_downline_count' })
  rightDownlinesCount?: number;

  @Expose({ name: 'child_count' })
  childCount?: number;

  constructor(
    id: number,
    parentId: number,
    position: number,
    status: UserStatus,
    username: string,
    avatar: string | undefined,
    pv: string,
    tv: string,
    cv: string,
    dateCreated: Date,
    rankId: number | undefined,
    rankName: string | undefined,
    rankIcon: string | undefined,
    packageName: string | undefined,
    firstname: string,
    lastname: string | undefined,
    level: number,
    totalEarnings: string,
    downlinesCount: number,
    leftPoints: string | undefined,
    rightPoints: string | undefined,
    leftCarry: string | undefined,
    rightCarry: string | undefined,
    leftDownlinesCount: number | undefined,
    rightDownlinesCount: number | undefined,
    childCount: number | undefined
  ) {
    this.id = id;
    this.parentId = parentId;
    this.position = position;
    this.status = status;
    this.username = username;
    this.avatar = avatar;
    this.pv = pv;
    this.tv = tv;
    this.cv = cv;
    this.dateCreated = dateCreated;
    this.rankId = rankId;
    this.rankName = rankName;
    this.rankIcon = rankIcon;
    this.packageName = packageName;
    this.firstname = firstname;
    this.lastname = lastname;
    this.level = level;
    this.totalEarnings = totalEarnings;
    this.downlinesCount = downlinesCount;
    this.leftPoints = leftPoints;
    this.rightPoints = rightPoints;
    this.leftCarry = leftCarry;
    this.rightCarry = rightCarry;
    this.leftDownlinesCount = leftDownlinesCount;
    this.rightDownlinesCount = rightDownlinesCount;
    this.childCount = childCount;
  }

  static fromJson(json: Record<string, any>): GenealogyUser {
    return plainToInstance(GenealogyUser, json);
  }

  toJson(): any {
    return instanceToPlain(this);
  }
}

export class GenealogyNode {
  name: string;

  @Type(() => GenealogyNode)
  children: GenealogyNode[];

  id: number;

  @Expose({ name: 'parent_id' })
  parentId: number;

  position: number;

  status: UserStatus;

  @Expose({ name: 'username' })
  username: string;

  @Expose({ name: 'first_name' })
  firstname: string;

  @Expose({ name: 'last_name' })
  lastname?: string;

  avatar?: string;

  pv: string;

  tv: string;

  cv: string;

  @Expose({ name: 'created_at' })
  @Type(() => Date)
  dateCreated: Date;

  @Expose({ name: 'rank_id' })
  rankId?: number;

  @Expose({ name: 'package_id' })
  packageId?: string;

  level: number;

  constructor(
    name: string,
    children: GenealogyNode[],
    id: number,
    parentId: number,
    position: number,
    status: UserStatus,
    username: string,
    firstname: string,
    lastname: string | undefined,
    avatar: string | undefined,
    pv: string,
    tv: string,
    cv: string,
    dateCreated: Date,
    rankId: number | undefined,
    packageId: string | undefined,
    level: number
  ) {
    this.name = name;
    this.children = children;
    this.id = id;
    this.parentId = parentId;
    this.position = position;
    this.status = status;
    this.username = username;
    this.firstname = firstname;
    this.lastname = lastname;
    this.avatar = avatar;
    this.pv = pv;
    this.tv = tv;
    this.cv = cv;
    this.dateCreated = dateCreated;
    this.rankId = rankId;
    this.packageId = packageId;
    this.level = level;
  }

  static fromJson(json: Record<string, any>): GenealogyNode {
    return plainToInstance(GenealogyNode, json);
  }

  toJson(): any {
    return instanceToPlain(this);
  }
}
